/*
 * InterviewReport — "Interview Feedback": the debrief after an AI interview,
 * a written interview or a rapid-fire round (and when reopened from history).
 *
 * It answers four questions from the evaluator's real output: what did I do
 * well, what did I do poorly, what should I improve, what should I practise
 * next — and links each weak topic back into Practice and the Knowledge Hub.
 */
import React, { useMemo, useRef, useState } from 'react';
import IvShell from './InterviewChrome';
import PracticeChooser from './PracticeChooser';
import { indexTree } from './paths';
import { ago, isAiType, reportScore, typeLabel } from './interviewData';
import { IconArrowRight, IconBook, IconCheck, IconChevDown, IconChevRight, IconAlert } from './icons';

const band = (s) => (s >= 80 ? 'strong' : s >= 60 ? 'good' : s >= 40 ? 'mid' : 'weak');
const BAND_LABEL = { strong: 'Strong performance', good: 'Good performance', mid: 'Getting there', weak: 'Needs work' };
const STATUS_CHIP = {
  correct: ['Solid', 'ok'], wrong: ['Needs work', 'bad'],
  too_slow: ['Too slow', 'slow'], skipped: ['Skipped', 'skip'],
};

const Ring = ({ value }) => {
  const C = 2 * Math.PI * 52;
  return (
    <svg viewBox="0 0 120 120" className={`irx-ring ${band(value)}`} aria-hidden="true">
      <circle cx="60" cy="60" r="52" className="irx-ring-track" />
      <circle cx="60" cy="60" r="52" className="irx-ring-val" strokeDasharray={`${(Math.max(0, Math.min(100, value)) / 100) * C} ${C}`} />
    </svg>
  );
};

const QuestionCard = ({ q, node, onPractice, onRead, open, onToggle }) => {
  const b = band(q.score || 0);
  const better = q.reference_answer || ((q.key_points || []).length ? null : '');
  return (
    <div className={`irx-q ${open ? 'open' : ''}`}>
      <button className="irx-q-head" type="button" onClick={onToggle} aria-expanded={open}>
        <span className="irx-q-num">Q{q.index || q.question_index}</span>
        <span className="irx-q-text">{q.question}</span>
        {q.status && STATUS_CHIP[q.status] && <span className={`irx-status ${STATUS_CHIP[q.status][1]}`}>{STATUS_CHIP[q.status][0]}</span>}
        <b className={`irx-score ${b}`} title="Question score, 0–100">{Math.round(q.score || 0)}<small>/100</small></b>
        {open ? <IconChevDown size={14} /> : <IconChevRight size={14} />}
      </button>
      {open && (
        <div className="irx-q-body">
          <div className="irx-sec"><h5>Your answer</h5><p className="irx-answer">{q.answer ? `“${q.answer}”` : 'No answer recorded.'}</p></div>
          {q.feedback && <div className="irx-sec"><h5>AI assessment</h5><p>{q.feedback}</p></div>}
          <div className="irx-cols">
            {(q.strengths || []).length > 0 && (
              <div className="irx-sec"><h5 className="ok">What worked</h5><ul className="irx-list ok">{q.strengths.map((x, i) => <li key={i}>{x}</li>)}</ul></div>
            )}
            {(q.improvements || []).length > 0 && (
              <div className="irx-sec"><h5 className="im">What could improve</h5><ul className="irx-list im">{q.improvements.map((x, i) => <li key={i}>{x}</li>)}</ul></div>
            )}
          </div>
          {(better || (q.key_points || []).length > 0) && (
            <div className="irx-sec irx-ref">
              <h5>Suggested stronger answer</h5>
              {better ? <p>{better}</p> : <ul className="irx-list">{q.key_points.map((x, i) => <li key={i}>{x}</li>)}</ul>}
            </div>
          )}
          {node && (
            <div className="irx-related">
              <span>Related topic: <b>{node.name}</b></span>
              <button type="button" className="pu-link" onClick={() => onRead(node)}>Read in Knowledge Hub</button>
              <button type="button" className="pu-btn pu-btn-sm pu-btn-quiet" onClick={() => onPractice(node)}>Practice this <IconArrowRight size={12} /></button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const InterviewReport = ({
  report, meta, hub, onPracticeWeak, onNewInterview, onDone, onStartPractice, onOpenHubNode, onRetry, ...shell
}) => {
  const [openQ, setOpenQ] = useState(0);
  const [chooser, setChooser] = useState(null);
  const feedbackRef = useRef(null);
  const byId = useMemo(() => indexTree(hub || []), [hub]);
  const byName = useMemo(() => {
    const m = {};
    Object.values(byId).forEach((n) => { if (n.name) m[n.name.toLowerCase()] = n; });
    return m;
  }, [byId]);

  const m = report?.metrics || {};
  const score = reportScore(report) ?? 0;
  const evals = report?.question_evaluations || [];
  const qMeta = report?.questions_meta || [];
  const recs = report?.recommendations || [];

  // Hub node for an id: the deepest existing prefix (concept ids can be deeper than the hub).
  const nodeForId = (cid) => {
    if (!cid) return null;
    const parts = String(cid).split('.');
    for (let i = parts.length; i > 0; i -= 1) { const n = byId[parts.slice(0, i).join('.')]; if (n) return n; }
    return null;
  };
  const nodeForName = (name) => (name ? byName[String(name).toLowerCase()] || null : null);
  const nodeForEval = (q, i) => nodeForId((q.concept_ids || [])[0]) || nodeForId(qMeta[i]?.concept) || nodeForName(q.topic || qMeta[i]?.topic);

  const concepts = (report?.concept_performance || []).map((c) => ({ ...c, node: nodeForId(c.concept_id) || nodeForName(c.concept) }));
  // Topic level (skill.topic) reads best in the breakdown — group questions under it.
  const topicOf = (n) => (n ? byId[String(n.id).split('.').slice(0, 2).join('.')] || n : null);
  const breakdown = concepts.length
    ? concepts.map((c) => ({ label: c.concept, score: c.average_score }))
    : Object.values(evals.reduce((acc, q, i) => {
      const t = topicOf(nodeForEval(q, i));
      const k = t ? t.id : `q${i}`;
      acc[k] = acc[k] || { label: t ? t.name : `Question ${q.index || i + 1}`, sum: 0, n: 0 };
      acc[k].sum += q.score || 0; acc[k].n += 1;
      return acc;
    }, {})).map((g) => ({ label: g.label, score: g.sum / g.n }));

  const strengths = [...new Set(evals.flatMap((q) => q.strengths || []))].slice(0, 8);
  const improvements = [...new Set(evals.flatMap((q) => q.improvements || []))].slice(0, 8);

  // Weak topics → practice suggestions (concept scores first, then weak questions by topic).
  const weak = useMemo(() => {
    const seen = new Map();
    concepts.filter((c) => c.average_score < 70 && c.node).forEach((c) => seen.set(c.node.id, { node: c.node, score: c.average_score, why: null }));
    evals.forEach((q, i) => {
      if ((q.score || 0) >= 70) return;
      const n = nodeForEval(q, i);
      if (!n) return;
      const cur = seen.get(n.id);
      if (!cur) seen.set(n.id, { node: n, score: q.score || 0, why: (q.improvements || [])[0] || null });
      else if (!cur.why) cur.why = (q.improvements || [])[0] || null;
    });
    return [...seen.values()].sort((a, b) => a.score - b.score).slice(0, 6);
  }, [concepts, evals]);   // eslint-disable-line react-hooks/exhaustive-deps

  const practise = (node) => setChooser({ title: node.name, subjects: [{ id: node.id, name: node.name, kind: 'topic' }] });
  const read = (node) => onOpenHubNode?.(node.id);
  const setup = meta?.setup;
  const kind = meta?.kind || (report?.rapidfire ? 'rapidfire' : report?.written ? 'written' : 'voice');

  // Learning loop: practise weak TOPICS, review the specific CONCEPTS behind them.
  const weakTopics = [...new Map(weak.map((w) => { const t = topicOf(w.node); return [t.id, { node: t, score: w.score, why: w.why }]; })).values()].slice(0, 3);
  const keyConcepts = weak.filter((w) => String(w.node.id).split('.').length > 2).slice(0, 3);
  const biggest = weakTopics[0];
  const scrollToReview = () => feedbackRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <IvShell hub={hub} className="iv-report" {...shell}
      overlay={chooser && (
        <PracticeChooser title={chooser.title} subjects={chooser.subjects}
          onStart={(o) => { setChooser(null); onStartPractice?.(o); }} onClose={() => setChooser(null)} />
      )}>
      <section className="ivx-head irx-head">
        <div>
          <button type="button" className="pu-link ivx-back" onClick={onDone}>← Interviews</button>
          <h1>Interview Feedback</h1>
          <p>Here’s how you did, why, and what to do next.</p>
          <div className="irx-meta">
            <span>{meta?.title || (report?.rapidfire ? 'Rapid-fire round' : 'AI Interview')}</span>
            {setup?.company && <span>{setup.company}</span>}
            <span>{typeLabel(kind)}</span>
            {setup?.level && <span>{setup.level}</span>}
            {meta?.ts && <span>{ago(meta.ts)}</span>}
          </div>
        </div>
        <div className="irx-head-acts">
          <button type="button" className="pu-btn pu-btn-quiet" onClick={scrollToReview}>Review answers</button>
          <button type="button" className="pu-btn pu-btn-primary" onClick={onNewInterview}>Try again <IconArrowRight size={13} /></button>
        </div>
      </section>

      {report?.error && (
        <div className="pu-panel irx-warn"><IconAlert size={16} /> The report couldn’t be generated ({report.error}). Your interview ended, but there’s nothing to score.</div>
      )}
      {report?.degraded && (
        <div className="pu-panel irx-warn"><IconAlert size={16} /> Scoring was briefly unavailable — these are placeholder scores, not a judgement of your answers.</div>
      )}

      <div className="irx-top">
        <section className="pu-panel irx-score-card" aria-label="Interview score">
          <div className="irx-ring-wrap">
            <Ring value={score} />
            <div className="irx-ring-label"><b>{score}<small>/100</small></b><span>Interview score</span></div>
          </div>
          <span className={`irx-band ${band(score)}`}>{BAND_LABEL[band(score)]}</span>
          {report?.overall_assessment && <p>{report.overall_assessment}</p>}
          <small className="irx-score-note">The average of your question scores, each graded 0–100.</small>
        </section>
        <section className="pu-panel irx-breakdown" aria-labelledby="irx-bd">
          <h2 id="irx-bd">Score breakdown</h2>
          <p className="pu-meta">By the {setup?.roleId ? 'role’s' : ''} topics this interview covered.</p>
          {breakdown.length ? (
            <ul>
              {breakdown.slice(0, 7).map((r, i) => (
                <li key={`${r.label}-${i}`}>
                  <span className="irx-bd-name">{r.label}</span>
                  <span className={`irx-bd-bar ${band(r.score)}`}><i style={{ width: `${Math.round(r.score)}%` }} /></span>
                  <b>{Math.round(r.score)}</b>
                </li>
              ))}
            </ul>
          ) : <p className="pu-empty">No scored answers in this interview.</p>}
          {report?.rapidfire && m.rapidfire_counts && (
            <div className="irx-rf">
              <span><b>{m.rapidfire_counts.correct ?? 0}</b> correct</span>
              <span><b>{m.rapidfire_counts.wrong ?? 0}</b> missed</span>
              <span><b>{m.rapidfire_counts.too_slow ?? 0}</b> too slow</span>
              <span><b>{m.rapidfire_counts.skipped ?? 0}</b> skipped</span>
            </div>
          )}
        </section>
      </div>

      <div className="irx-sw">
        <section className="pu-panel" aria-labelledby="irx-str">
          <h2 id="irx-str">Strengths</h2>
          {strengths.length ? <ul className="irx-ticks ok">{strengths.slice(0, 5).map((x, i) => <li key={i}><IconCheck size={13} />{x}</li>)}</ul>
            : <p className="pu-empty">The evaluator didn’t call out specific strengths this time.</p>}
        </section>
        <section className="pu-panel" aria-labelledby="irx-imp">
          <h2 id="irx-imp">Areas to improve</h2>
          {improvements.length ? <ul className="irx-ticks im">{improvements.slice(0, 5).map((x, i) => <li key={i}><span aria-hidden="true">×</span>{x}</li>)}</ul>
            : <p className="pu-empty">No specific improvements were flagged.</p>}
          {recs.length > 0 && <p className="irx-advice"><b>Interviewer’s advice:</b> {recs[0]}</p>}
        </section>
      </div>

      <section className="pu-panel irx-review" aria-labelledby="irx-qq" ref={feedbackRef}>
        <h2 id="irx-qq">Question-by-question review <small>{evals.length} questions</small></h2>
        {evals.length ? evals.map((q, i) => (
          <QuestionCard key={q.index || q.question_index || i} q={q} node={nodeForEval(q, i)}
            open={openQ === i} onToggle={() => setOpenQ(openQ === i ? -1 : i)} onPractice={practise} onRead={read} />
        )) : <p className="pu-empty">No questions were evaluated.</p>}
      </section>

      <section className="pu-panel irx-next2" aria-labelledby="irx-next">
        <h2 id="irx-next">Recommended next steps</h2>
        {biggest ? (
          <div className="irx-biggest">
            <div>
              <small>Your biggest opportunity</small>
              <b>{biggest.node.name}</b>
              <span>{biggest.why || `Scored ${Math.round(biggest.score)}/100 in this interview`}</span>
            </div>
            <button type="button" className="pu-btn pu-btn-primary" onClick={() => practise(biggest.node)}>Practice this <IconArrowRight size={13} /></button>
          </div>
        ) : <p className="pu-empty"><IconCheck size={13} /> Nothing scored below 70 — keep the momentum with a harder interview.</p>}
        <div className="irx-steps">
          <div>
            <h3><span>1</span>Practice weak areas</h3>
            {weakTopics.length ? (
              <ul>{weakTopics.map((w) => (
                <li key={w.node.id}><button type="button" className="pu-link" onClick={() => practise(w.node)}>{w.node.name} <IconArrowRight size={12} /></button><small>{Math.round(w.score)}/100 in this interview</small></li>
              ))}</ul>
            ) : <p className="pu-meta">No weak areas this time.</p>}
            {onPracticeWeak && <button type="button" className="pu-link irx-small" onClick={onPracticeWeak}>Plan a full practice session</button>}
          </div>
          <div>
            <h3><span>2</span>Review key concepts</h3>
            {(keyConcepts.length ? keyConcepts : weakTopics).length ? (
              <ul>{(keyConcepts.length ? keyConcepts : weakTopics).map((w) => (
                <li key={w.node.id}><button type="button" className="pu-link" onClick={() => read(w.node)}><IconBook size={12} /> {w.node.name}</button><small>Review in Knowledge Hub</small></li>
              ))}</ul>
            ) : <p className="pu-meta">Nothing flagged to review.</p>}
          </div>
          <div>
            <h3><span>3</span>Try another interview</h3>
            <p className="pu-meta">{meta?.title || 'Same role'}{setup?.level ? ` · ${setup.level}` : ''} · Adaptive difficulty{weakTopics.length ? `, focused on ${weakTopics.map((w) => w.node.name).join(', ')}` : ''}.</p>
            {setup && kind !== 'library'
              ? <button type="button" className="pu-btn pu-btn-sm pu-btn-quiet" onClick={() => onRetry(weakTopics.length ? { ...setup, recommended: false, difficulty: null, focus: weakTopics.map((w) => ({ id: w.node.id, name: w.node.name })) } : setup)}>Set it up <IconArrowRight size={12} /></button>
              : <button type="button" className="pu-btn pu-btn-sm pu-btn-quiet" onClick={onNewInterview}>Start another <IconArrowRight size={12} /></button>}
          </div>
        </div>
        <p className="pu-meta irx-mem">{isAiType(kind) || kind === 'written' ? 'Your answers from this interview are already part of your Learning Memory on the Progress page.' : ''}</p>
      </section>
    </IvShell>
  );
};

export default InterviewReport;
